import { Component, Element, Event, EventEmitter, h, Host } from "@stencil/core";
import api from "../../utils/api";
import { getAttributesCamelCase } from "../../utils/getAttributesCamelCase";
import { filterObject } from "../../utils/filterObject";

/** these common HTML attributes will not be considered for fetching data */
const ignoreAttributeList = ["class", "id", "style", "slot", "tabindex", "title"];

@Component({
    tag: "ux-data-ingest",
    styleUrl: "data-ingest.scss",
    shadow: false
})
export class DataIngest {
    /** Is fired when a request is sent with the name of the property */
    @Event({ eventName: "uxStartRequest" }) startRequest: EventEmitter<string>;

    @Element() host: HTMLUxDataIngestElement;
    /** contains the filtered attributes with their URLs */
    private attributes: object = {};
    /** observes changes to the attributes */
    private observer: MutationObserver;

    componentWillLoad() {
        this.refreshAttributes();
        this.observer = new MutationObserver(() => this.refreshAttributes());
        this.observer.observe(this.host, {
            attributes: true,
            childList: false,
            subtree: false
        });
    }

    disconnectedCallback() {
        this.observer.disconnect();
    }

    /** reads the attributes and their URLs and matches them with the previous ones */
    private refreshAttributes() {
        const oldAttributes = this.attributes;
        this.attributes = filterObject(getAttributesCamelCase(this.host), ignoreAttributeList);
        Object.keys(this.attributes).forEach((key) => {
            if (this.attributes[key] !== oldAttributes[key]) {
                this.loadData(key);
            }
        });
    }

    /** loads the data from the url supplied via attribute to put it onto the child element */
    private loadData(attributeName: string) {
        const url = this.attributes[attributeName];
        if (url === "") {
            return;
        }
        if (url.charAt(0) === "#") {
            return this.getScriptTag(attributeName);
        }
        this.startRequest.emit(attributeName);
        const child = this.host.firstElementChild;
        const loading = `${attributeName}Loading`;
        if (child === null) {
            return;
        }
        child[loading] = true;
        api(url)
            .then((result) => {
                child[loading] = false;
                child[attributeName] = result;
            })
            .catch(() => {
                child[loading] = false;
            });
    }

    private getScriptTag(attributeName: string) {
        const url = this.attributes[attributeName] || "";
        // we decided to use data-id instead of id because it can not always be ensured that the id is unique.
        const tag: HTMLScriptElement = this.host.querySelector(`[data-id="${url.replace("#", "")}"]`);
        if (url.length < 2 || tag === null) {
            return;
        }
        const content = tag.innerHTML;
        let result;
        if (tag.type === "application/json") {
            result = JSON.parse(content);
        }
        if (tag.type === "text/plain") {
            result = String(content).trim();
        }
        const child = this.host.firstElementChild;
        if (child === null) {
            return;
        }
        child[attributeName] = result;
    }

    render() {
        return (
            <Host class="ux-data-ingest">
                <slot />
            </Host>
        );
    }
}
